/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../dataSources/metadata/MetadataColumn',
	'bi/moser/moser.min'
], (
	_,
	MetadataColumn,
	MoserJS
) => {

	const SUPPORTED_SOURCE_CATEGORY_TYPES = [
		MoserJS.default.SourceCategoryType.HIERARCHY_LEVEL,
		MoserJS.default.SourceCategoryType.HIERARCHY_PARENT_CHILD,//member based hierachy, always return a hieararchy since there are no levels
		MoserJS.default.SourceCategoryType.LEVEL,
		MoserJS.default.SourceCategoryType.COLUMN //@todo return null what do we do so does it  break R3?
	];

	const ANCESTOR_TYPES = [MoserJS.default.MoserObjectTypes.QUERY_ITEM];

	/**
	 * Limitations enforce by DSS:
	 *
	 * Cannot drop level and hierarchy to same slot
	 * Cannot drop multiple hiearachies in same dimension: eg Years and Currently
	 * Can drop different hieararchies from differnt dimensions : eg Years and Product
	 *
	 * Just document the limitations here since these should have already handle by drag and drop in R3
	*/

	return class MetadataDNDMapping {
		constructor(moduleAPI, dashboard, options = {}) {
			this.moduleAPI = moduleAPI;
			this.dashboard = dashboard;
			this.dataSource = this.dashboard.getFeature('DataSources').getDataSource(moduleAPI.getSourceId());
			this.moserObjects = options.moserObjects || [];
			this.dNDResults = [];
			this._processMetadataMapping();
		}

		getMappedResults () {
			return this.dNDResults;
		}

		getUtils() {
			return {
				getColumnsWithMembers: this.getColumnsWithMembers.bind(this)
			};
		}

		getColumnsWithMembers ( filterMemberColumns = false) {
			let columnsWithMembers = {};
			_.each(this.dNDResults, result => {
				const metadataColumn = result.mappedMetadataColumn;
				const members = result.members;
				// Refer to members since it's mapped from a member.
				if (metadataColumn) {
					if (!filterMemberColumns || members.length) {
						columnsWithMembers[metadataColumn.getId()] = members;
					}
				}
			});
			return columnsWithMembers;
		}

		_processMetadataMapping () {
			this.moserObjects.forEach(moserObject => {
				const payload = {};
				let sourceCategory;
				let mappedMoserObject;
				if (!this._isMemberType(moserObject)) {
					mappedMoserObject = moserObject;
				} else {
					const searchMoserObject	= this._memberSearchSubject();
					mappedMoserObject = this._getMappedMoserObject(moserObject, searchMoserObject);
					if (mappedMoserObject) {
						sourceCategory = this._getSourceCategory(mappedMoserObject);
						if (SUPPORTED_SOURCE_CATEGORY_TYPES.includes(sourceCategory)) {
							payload.members = {
								id: moserObject.getIdForExpression(),
								memberCaption: moserObject.getMemberCaption(), //example Camping Equipment
								calculatedMemberType: moserObject.calculatedMemberType && moserObject.calculatedMemberType.value()
							};
							mappedMoserObject = this._normalizeMappedMoserObject(mappedMoserObject, moserObject, sourceCategory);

						} else {
							//@todo not sure what this this so add to be save
							mappedMoserObject = moserObject;
						}
					} else {
						/*Get here when one of the following is true:
						 *
						 * 1. Select Product line from a relation source
						 * 2. A measure like Revenue from relational source
						 * 3. A measure like Revenue from OLAP source
						*/

						mappedMoserObject = moserObject;
					}
				}

				// query item from navigation group is a referenced object
				let referencedObject = mappedMoserObject.getReferencedObject ? mappedMoserObject.getReferencedObject() : null;
				if (referencedObject) {
					payload.referencedObject = this.dataSource.getMetadataColumn(referencedObject.getIdForExpression());
				}
				payload.mappedMetadataColumn = this.dataSource.getMetadataColumn(mappedMoserObject.getIdForExpression());
				this._addDNDResult(payload);
			});
		}

		_getSourceCategory(mappedMoserObject) {
			let sourceCategory = mappedMoserObject.getSourceCategory();
			if (!sourceCategory && mappedMoserObject.getObjectType() === MoserJS.default.MoserObjectTypes.QUERY_ITEM ) {
				sourceCategory = MoserJS.default.SourceCategoryType.COLUMN;
			}
			return sourceCategory;
		}

		_getMappedMoserObject(moserObject, searchMoserObject) {
			if (!searchMoserObject) {
				//Note drag members from the rootMember, this function also return a level so it treats rootMember as like any other regular level
				return MoserJS.default.ModuleUtils.getTreeAncestor(moserObject, ANCESTOR_TYPES);
			} else if (searchMoserObject && this._isMemberType(searchMoserObject)) {
				return MoserJS.default.ModuleUtils.getTreeAncestor(searchMoserObject, ANCESTOR_TYPES);
			}
			return searchMoserObject;
		}

		_memberSearchSubject() {
			return this.moduleAPI.getShapingHelper().getMemberSearchSubject();
		}

		_isMemberType (moserObject) {
			return moserObject.getObjectType() === 'MemberType';
		}

		_normalizeMappedMoserObject (mappedMoserObject, moserObject, sourceCategory) {
			if (sourceCategory === MoserJS.default.SourceCategoryType.LEVEL ) {
				const metadataColumn = this.moduleAPI.getMetadataColumn(moserObject.getLevelUniqueName());
				return metadataColumn.moserObject;
			}
			return mappedMoserObject;
		}

		_addDNDResult (payload = {}) {
			const dnDResult = this.dNDResults.find(result => {
				if (payload.mappedMetadataColumn && result.mappedMetadataColumn) {
					return result.mappedMetadataColumn.getId() === payload.mappedMetadataColumn.getId();
				} else {
					return false;
				}
			});

			if (dnDResult) {
				if (payload.members) {
					// Add local filter payload has filter built from member.
					dnDResult.members.push(payload.members);
				}
			} else {
				payload.members = payload.members ? [payload.members] : [];

				this.dNDResults.push(payload);
			}
		}
	};
});
